import * as Yup from 'yup';
import ReactHtmlParser from 'react-html-parser';

import regexList from '../../regex/regexList';

import {
  FieldType,
  FormFieldCssClassType,
  FormFieldInputType,
  FormFieldT,
  FormFieldType,
} from './types';

export const getFormFieldProperties = (formFields: Record<string, any>[]) => {
  const fieldProperties = formFields.reduce<FormFieldT[]>((acc, formField) => {
    const type = formField.fieldType;

    if (!type || type === '') {
      return acc;
    }
    const properties = {
      ...formField,
      __typename: formField.__typename,
      contentful_id: formField.contentful_id,
      showError: true,
      errorMessage: formField.errorMessage,
      schemaName: formField.fieldName,
    };
    switch (type) {
      case FieldType.RECAPTCHA:
        acc.push({
          ...properties,
          type: FormFieldType.RECAPTCHA,
          className: FormFieldCssClassType.RECAPTCHA,
          schemaName: 'gRecaptchaResponse',
          fieldName: 'gRecaptchaResponse',
        });
        return acc;
      case FieldType.DROPDOWN:
        acc.push({
          ...properties,
          type: FormFieldType.DROPDOWN,
          inputType: FormFieldInputType.INPUT,
          className: FormFieldCssClassType.HALF,
        });
        return acc;
      case FieldType.PHONE:
        acc.push({
          ...properties,
          type: FormFieldType.TEXTFIELD,
          inputType: FormFieldInputType.TEL,
          maxLength: 20,
          minLength: 10,
          className: FormFieldCssClassType.HALF,
        });
        return acc;

      case FieldType.CHECKBOX:
        acc.push({
          ...properties,
          type: FormFieldType.CHECKBOX,
          inputType: FormFieldInputType.CHECKBOX,
          className: FormFieldCssClassType.OPTIN,
          schemaName: 'customerOptIn',
          fieldName: 'customerOptIn',
        });
        return acc;
      case FieldType.EMAIL:
        acc.push({
          ...properties,
          type: FormFieldType.TEXTFIELD,
          inputType: FormFieldInputType.EMAIL,
          maxLength: 320,
          minLength: 3,
          className: FormFieldCssClassType.HALF,
        });
        return acc;
      case FieldType.TEXT:
        acc.push({
          ...properties,
          type: FormFieldType.TEXTFIELD,
          inputType: FormFieldInputType.INPUT,
          maxLength: 25,
          minLength: 2,
          className: FormFieldCssClassType.HALF,
        });
        return acc;
      default:
        return acc;
    }
  }, []);
  return fieldProperties;
};

export const generateValidationSchema = (formFields: Record<string, any>[]) => {
  const schema = formFields.reduce<Record<string, any>>((acc, formField) => {
    const { fieldType: type } = formField;
    if (!type || type === '') {
      return acc;
    }
    if (type === FieldType.RECAPTCHA) {
      // Recaptcha field
      return {
        ...acc,
        gRecaptchaResponse: Yup.string()
          .required(ReactHtmlParser(formField).errorMessage)
          .nullable(),
      };
    }
    if (type === FieldType.DROPDOWN) {
      return acc;
    }
    if (type === FieldType.PHONE) {
      return {
        ...acc,
        [formField.fieldName]: Yup.string()
          .matches(regexList.phone, ReactHtmlParser(formField).errorMessage)
          .min(10, ReactHtmlParser(formField).errorMessage)
          .required(ReactHtmlParser(formField).errorMessage),
      };
    }
    if (type === FieldType.EMAIL) {
      return {
        ...acc,
        [formField.fieldName]: Yup.string()
          .email(ReactHtmlParser(formField).errorMessage)
          .required(ReactHtmlParser(formField).errorMessage),
      };
    }
    if (type === FieldType.CHECKBOX) {
      return {
        ...acc,
        [formField.fieldName]: Yup.bool().oneOf([true], 'required').required(),
      };
    }
    if (type === FieldType.TEXT) {
      // Name fields
      return {
        ...acc,
        [formField.fieldName]: Yup.string()
          .matches(
            regexList.customerName,
            ReactHtmlParser(formField).errorMessage,
          )
          .required(ReactHtmlParser(formField).errorMessage),
      };
    }
    // If unsupported, skip it
    return acc;
  }, {});
  return schema;
};

export const getPreselectedDropdownValue = (
  fieldName?: string,
  fieldValues?: string[],
  preselectedValues?: URLSearchParams,
) => {
  if (!fieldName) return undefined;
  const preselectedValue = preselectedValues?.get(fieldName);
  if (preselectedValue && fieldValues?.indexOf(preselectedValue) !== -1) {
    return preselectedValue;
  }
  return undefined;
};
